import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { APPRECIATIONS_AEA } from '../utils/cycle.helpers';

export default function BulletinPrescolaire() {
  const { obsId } = useParams();
  const navigate = useNavigate();
  const [obs, setObs] = useState(null);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [school, setSchool] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/registry/observations-prescolaire').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      const found = all.find((o) => o.id === obsId);
      if (found) {
        setObs(found);
        // Charger les élèves
        api.get('/students').then((allStudents) => {
          const inClass = (allStudents || []).filter((s) =>
            (s.enrollments || []).some((en) => en.classId === found.classId)
          );
          setStudents(inClass);
          if (inClass[0]) setSelected(inClass[0].id);
        }).catch(() => {});
      }
    }).catch((e) => setError(e.message));
    api.get('/establishment').then(setSchool).catch(() => {});
  }, [obsId]);

  if (error) return <div className="form-error">{error}</div>;
  if (!obs) return <div className="loading">Chargement…</div>;

  const student = students.find((s) => s.id === selected);
  const studentObs = obs.observations?.[selected] || {};
  const domaines = obs.domaines || [];

  function getAppr(code) {
    const val = studentObs[code]?.appr || '';
    return APPRECIATIONS_AEA.find((a) => a.code === val) || null;
  }

  const dateLabel = obs.date ? new Date(obs.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' }) : '—';

  return (
    <>
      {/* Sélecteur élève + actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <button className="btn-ghost btn-sm" onClick={() => navigate(-1)}>← Retour</button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={selected} onChange={(e) => setSelected(e.target.value)} style={{ width: 200 }}>
            {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
          </select>
          <button className="btn-ghost btn-sm" onClick={() => window.print()}>🖨 Imprimer</button>
        </div>
      </div>

      {/* Bulletin imprimable */}
      <div className="bulletin" style={{ maxWidth: 700 }}>
        {/* En-tête */}
        <div className="bulletin-head">
          <div className="bulletin-mark">S</div>
          <div className="bulletin-school">
            <strong>{school?.name || 'Groupe Scolaire La Providence'}</strong>
            <span>{school?.address || ''}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="bulletin-title">Bulletin Pré-Scolaire</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{dateLabel}</div>
          </div>
        </div>

        {/* Identité élève */}
        <div className="bulletin-identity" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 16 }}>
          <div>
            <span>Nom & Prénom</span>
            <b>{student?.lastName} {student?.firstName}</b>
          </div>
          <div>
            <span>Matricule</span>
            <b>{student?.matricule || '—'}</b>
          </div>
          <div>
            <span>Classe</span>
            <b>{obs.className || '—'}</b>
          </div>
        </div>

        {/* Légende */}
        <div style={{ display: 'flex', gap: 10, margin: '14px 0 10px', flexWrap: 'wrap' }}>
          {APPRECIATIONS_AEA.map((a) => (
            <span key={a.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
              <span style={{ width: 20, height: 20, borderRadius: 4, background: a.color, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700 }}>{a.code}</span>
              {a.label}
            </span>
          ))}
        </div>

        {/* Tableau domaines */}
        <table className="bulletin-table">
          <thead>
            <tr>
              <th style={{ width: '55%' }}>Domaine d'activités / Matière</th>
              <th style={{ textAlign: 'center', width: 60 }}>A</th>
              <th style={{ textAlign: 'center', width: 60 }}>EA</th>
              <th style={{ textAlign: 'center', width: 60 }}>NA</th>
              <th>Observation</th>
            </tr>
          </thead>
          <tbody>
            {domaines.map((code) => {
              const appr = getAppr(code);
              // Trouver le label depuis les domaines de l'observation
              const domaineLabel = obs.domainesLabels?.[code] || code;
              return (
                <tr key={code}>
                  <td>{domaineLabel}</td>
                  {APPRECIATIONS_AEA.map((a) => (
                    <td key={a.code} style={{ textAlign: 'center' }}>
                      {appr?.code === a.code ? (
                        <span style={{ display: 'inline-block', width: 22, height: 22, borderRadius: 5, background: a.color, color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '22px', textAlign: 'center' }}>✓</span>
                      ) : ''}
                    </td>
                  ))}
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{studentObs[code]?.comment || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Appréciation générale */}
        <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--surface-soft)', borderRadius: 8, border: '0.5px solid var(--line)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
            Appréciation générale de l'enseignant(e)
          </div>
          <div style={{ fontSize: 13, minHeight: 40 }}>
            {studentObs.GENERAL?.comment || obs.commentaireGeneral || '—'}
          </div>
        </div>

        {/* Absences */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 14 }}>
          {[['Absences justifiées', '—'], ['Absences injustifiées', '—'], ['Retards', '—']].map(([label, val]) => (
            <div key={label} style={{ textAlign: 'center', padding: '10px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Signatures */}
        <div className="bulletin-foot" style={{ marginTop: 32 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 40 }}>L'enseignant(e)</div>
            <div className="sign-line" style={{ margin: '0 auto' }} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 40 }}>Le Directeur / La Directrice</div>
            <div className="sign-line" style={{ margin: '0 auto' }} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 40 }}>Les Parents / Tuteurs</div>
            <div className="sign-line" style={{ margin: '0 auto' }} />
          </div>
        </div>
      </div>
    </>
  );
}
